import { useCallback } from "react"
import { useMutation } from "urql"

import {
  AddPurchasingCostDocument,
  type AddPurchasingCostFailure,
  type AddPurchasingCostInput,
  type AddPurchasingCostMutation,
} from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

import "../graphql/mutations"

interface UseCreatePurchasingCostReturn {
  createPurchasingCost: (input: AddPurchasingCostInput) => Promise<boolean>
  isCreating: boolean
}

export const useCreatePurchasingCost = (): UseCreatePurchasingCostReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching }, executeMutation] = useMutation<AddPurchasingCostMutation, { input: AddPurchasingCostInput }>(
    AddPurchasingCostDocument
  )

  const createPurchasingCost = useCallback(
    async (input: AddPurchasingCostInput): Promise<boolean> => {
      const { data, error } = await executeMutation({ input })
      const output = data?.priceChange.addPurchasingCost
      const hasError = error || output?.__typename === "AddPurchasingCostFailure"

      if (hasError) {
        const message = error?.message ?? (output as AddPurchasingCostFailure).error.message
        notify({ type: NotificationType.Error, message })
        return false
      }

      notify({ type: NotificationType.Success, message: "Purchasing cost added" })
      return true
    },
    [executeMutation, notify]
  )

  return { createPurchasingCost, isCreating: fetching }
}
